import React, { createContext, useState, useContext, useEffect } from 'react';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BrandingContext = createContext();

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error('useBranding must be used within BrandingProvider');
  }
  return context;
};

export const BrandingProvider = ({ children }) => {
  const [branding, setBranding] = useState({
    company_name: 'Goldpeak Financial Solutions',
    tagline: 'Complete Loan DSA CRM Platform',
    logo_url: '',
    primary_color: '#D4AF37',
    secondary_color: '#0F172A',
    sidebar_color: '#FFFFFF',
    dashboard_color: '#F9FAFB',
    address: '',
    email: '',
    phone: '',
    website: '',
  });

  const hexToHsl = (hex) => {
    const r = parseInt(hex.slice(1, 3), 16) / 255;
    const g = parseInt(hex.slice(3, 5), 16) / 255;
    const b = parseInt(hex.slice(5, 7), 16) / 255;
    const max = Math.max(r, g, b);
    const min = Math.min(r, g, b);
    let h = 0, s = 0;
    const l = (max + min) / 2;
    if (max !== min) {
      const d = max - min;
      s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
      if (max === r) h = ((g - b) / d + (g < b ? 6 : 0)) * 60;
      else if (max === g) h = ((b - r) / d + 2) * 60;
      else h = ((r - g) / d + 4) * 60;
    }
    return `${Math.round(h)} ${Math.round(s * 100)}% ${Math.round(l * 100)}%`;
  };

  const applyThemeColors = (data) => {
    if (data.primary_color) {
      const hsl = hexToHsl(data.primary_color);
      document.documentElement.style.setProperty('--primary', hsl);
      document.documentElement.style.setProperty('--ring', hsl);
      document.documentElement.style.setProperty('--sidebar-active', hsl);
    }
    if (data.sidebar_color) {
      const hsl = hexToHsl(data.sidebar_color);
      document.documentElement.style.setProperty('--sidebar', hsl);
    }
    if (data.secondary_color) {
      const hsl = hexToHsl(data.secondary_color);
      document.documentElement.style.setProperty('--secondary', hsl);
    }
  };

  const loadBranding = async () => {
    try {
      const response = await axios.get(`${API}/settings/branding/public`);
      setBranding((prev) => ({ ...prev, ...response.data }));
      applyThemeColors(response.data);
    } catch (error) {
      console.error('Failed to load branding:', error);
    }
  };

  const loadFullBranding = async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) return;
      const response = await axios.get(`${API}/settings/branding`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setBranding((prev) => ({ ...prev, ...response.data }));
      applyThemeColors(response.data);
    } catch (error) {
      console.error('Failed to load full branding:', error);
    }
  };

  const getLogoUrl = () => {
    if (branding.logo_url) {
      if (branding.logo_url.startsWith('http')) return branding.logo_url;
      return `${process.env.REACT_APP_BACKEND_URL}${branding.logo_url}`;
    }
    return 'https://static.prod-images.emergentagent.com/jobs/28e64fab-4b49-47e2-81b4-57b8629d4478/images/1174466323784e231c35c8d2fcfb2110db326bdbf93e5866708a62daa98ae548.png';
  };

  useEffect(() => {
    loadBranding();
  }, []);

  return (
    <BrandingContext.Provider value={{ branding, loadBranding, loadFullBranding, getLogoUrl, setBranding }}>
      {children}
    </BrandingContext.Provider>
  );
};
